/**
 * The application root.
 *
 * One provider stack, one session, one router — established once here and
 * shared by every module.
 */

import { useEffect } from 'react';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfirmProvider, Toaster } from '@/modules/accounting/components/ui';
import { setSessionExpiredHandler } from '@/lib/api';
import { useSession } from '@/stores/session';
import { AppRoutes } from './router';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { onDataChanged } from '@/lib/dataChanged';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 is handled by signing out, and a 403 will not succeed on a
      // retry either — only genuine failures are worth trying again.
      retry: (failureCount, error) => {
        const status = (error as { status?: number })?.status;
        if (status && status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

// A save anywhere makes every open list and figure fetch again.
onDataChanged(() => { void queryClient.invalidateQueries(); });

/**
 * Restores the session on load and wires the "your session ended" path, so an
 * expired token takes the user to the login page instead of leaving them on a
 * screen whose requests silently fail.
 */
function SessionGate({ children }: { children: React.ReactNode }) {
  const bootstrap = useSession((s) => s.bootstrap);
  const navigate = useNavigate();

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      useSession.setState({
        user: null, company: null, companies: [],
        financialYear: null, fy: null, financialYears: [], reference: null,
        permissions: { superadmin: false, map: {} },
        ready: true,
      });
      navigate('/login', { replace: true });
    });
  }, [navigate]);

  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* One confirmation dialog and one notifier for every module. */}
      <ConfirmProvider>
        <BrowserRouter>
          <SessionGate>
            <AppRoutes />
          </SessionGate>
        </BrowserRouter>
        {/* A malformed notification must never blank the application. */}
        <ErrorBoundary silent>
          <Toaster />
        </ErrorBoundary>
      </ConfirmProvider>
    </QueryClientProvider>
  );
}
